import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { initI18n } from "./i18n";
import { CheckCircleIcon, ChevronRightIcon } from "@heroicons/react/outline";
import AppOnboardingWorkflows from "./AppOnboardingWorkflows";
import AppOnboardingData from "./AppOnboardingData";
import AppOnboardingGenerating from "./AppOnboardingGenerating";
import AppOnboardingConnect from "./AppOnboardingConnect";
import AppOnboardingPreview from "./AppOnboardingPreview";
import { WorkflowAutomation } from "./interface";

const STEP_CONNECT = 0;
const STEP_WORKFLOW = 1;
const STEP_DATA = 2;
const STEP_GENERATE = 3;
const STEP_PREVIEW = 4;

initI18n();

interface Props {
  initalStep?: number;
}

function AppOnboarding(props: Props) {
  const { t, i18n } = useTranslation();

  const query = new URLSearchParams(window.location.search);

  const token = query.get('token') || null;
  const language = query.get('lang') || "en";

  const [selectedWorkflow, setSelectedWorkflow] = useState<string>();
  const [currentAutomation, setCurrentAutomation] = useState<WorkflowAutomation>();

  function startAutomation(objectId: string) {
    setCurrentAutomation(undefined) // clear current automation

    fetch(`${process.env.REACT_APP_API_ENDPOINT}/hubspot/onboarding/automations/`, {
      method: "POST",
      headers: {
        'Content-Type': 'application/json',
        "X-Token": token!
      },
      body: JSON.stringify({ slug: selectedWorkflow, objectId })
    })
      .then(res => res.json())
      .then(setCurrentAutomation);
  }

  useEffect(() => {
    i18n.changeLanguage(language);
  }, [language, i18n]);

  const [stepIndex, setStepIndex] = useState(props.initalStep ?? STEP_CONNECT);
  const steps = ["Connect Hubspot", "Choose Template", "Select Data", "Generate Document"]

  if (stepIndex === STEP_PREVIEW)
    return <AppOnboardingPreview selectedWorkflow={selectedWorkflow ?? "contracts"} automationItem={currentAutomation?.items?.[0]} onClickBack={() => setStepIndex(STEP_WORKFLOW)} />

  return (
    <div className="flex flex-col w-screen h-screen items-center gap-20 pt-32">
      <img src='https://static.portant.co/logo-full.svg' alt='Portant Logo' className="w-32 absolute left-10 top-10" />
      <div className="flex items-center text-sm">
        {steps.map((step, index) => (
          <div key={step} className="flex items-center gap-1">
            <CheckCircleIcon className={`w-4 h-4 ${"invisible"}`} />  {/* Invisible icon to balance widths */}
            <div className={`font-semibold ${stepIndex === index ? "text-blue" : (stepIndex > index ? "text-green" : "text-gray-400")}`}>
              {t(step)}
            </div>
            <CheckCircleIcon className={`w-4 h-4 ${stepIndex > index ? "text-green" : "invisible"}`} />
            {index !== (steps.length - 1) &&
              <ChevronRightIcon className="w-6 h-6 text-gray-400 mx-2" />
            }
          </div>
        ))}
      </div>
      {stepIndex === STEP_CONNECT &&
        <AppOnboardingConnect />
      }
      {stepIndex === STEP_WORKFLOW &&
        <AppOnboardingWorkflows token={token!} onSelectWorkflow={(slug) => { setSelectedWorkflow(slug); setStepIndex(STEP_DATA); }} />
      }
      {stepIndex === STEP_DATA &&
        <AppOnboardingData token={token!} onClickBack={() => setStepIndex(1)} onGenerateDocument={(objectId) => { startAutomation(objectId); setStepIndex(STEP_GENERATE) }} />
      }
      {stepIndex === STEP_GENERATE &&
        <AppOnboardingGenerating token={token!} selectedWorkflow={selectedWorkflow ?? "contracts"} initialAutomation={currentAutomation} onCompleteAutomation={automation => { setCurrentAutomation(automation); setStepIndex(STEP_PREVIEW) }} />
      }
    </div>
  );
}

export default AppOnboarding;
